import { motion } from 'framer-motion';
import { Button, Typography, Box } from '@mui/material';
import CopyableField from './CopyableField';
import { useEffect, useState } from 'react';
import LoadingPage from './atoms/LoadingPage';
import ErrorContent from './atoms/ErrorPage';
import ApiGuidelines from './atoms/APIGuidelines';
import CredentialInstructions from './atoms/CredentialInstructions';
import { ApiSubscriptionsThumbSVGIcon, NotesSVGIcon, SearchThinSVGIcon } from '../Utilities/SvgHelper';
import { INTEGRATION_URLS } from '../common/Constants';

const SuccessContent = ({ redirectUserToApp }) => {
  const [clientId, setClientId] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [userApiKey, setUserApiKey] = useState(null);
  const [showLoader, setShowLoader] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.addEventListener('message', event => {
      if (event?.origin !== process.env.REACT_APP_CHECKOUT_PAGE_URL) {
        console.warn('Unknown origin found for iframe message:', event.origin);
        return;
      }

      const receivedAction = event.data;

      if (receivedAction?.type === 'CREDENTIALS_RECEIVED') {
        setClientId(receivedAction?.payload.clientId);
        setClientSecret(receivedAction?.payload.clientSecret);
        setUserApiKey(receivedAction?.payload.userApiKey);
        setShowLoader(false);
      }
    });
  }, []);

  if (error && !clientId && !clientSecret) {
    return <ErrorContent redirectUserToApp={redirectUserToApp} />;
  }

  return (
    <div
      style={{
        maxWidth: '960px',
        margin: '0 auto',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, type: 'spring', bounce: 0.4 }}
        style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center', paddingTop: '60px' }}>
          <Typography sx={{ fontSize: '40px', fontWeight: '600' }} color="#2d2d2d">
            Achievement unlocked!
          </Typography>
          <Typography sx={{ maxWidth: '570px', textAlign: 'center', fontSize: '18px' }} color="#2d2d2d">
            You're one step away from making your first API call.
          </Typography>
        </Box>
        {/* <ApiGuidelines /> */}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.5 }}
        style={{ width: '100%' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '80px',
            alignSelf: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="button"
            onClick={() => window.open(process.env.REACT_APP_API_DOCS_URL, '_blank')}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '16px',
              alignItems: 'center',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <Box
              sx={{
                width: '84px',
                height: '84px',
                backgroundColor: '#6F47CB',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'grey.0',
                transition: 'background-color 0.2s ease',
                '& svg': {
                  width: '60px',
                  height: '60px',
                },
                'button:hover &': {
                  backgroundColor: '#5939A2',
                },
              }}
            >
              <NotesSVGIcon />
            </Box>
            <Typography
              color="grey.9999"
              sx={{
                fontWeight: '400',
                maxWidth: '220px',
                textAlign: 'left',
                fontSize: '16px',
                textDecoration: 'none',
                'button:hover &': {
                  textDecoration: 'underline',
                },
              }}
            >
              Access our developer docs for usage guidelines
            </Typography>
          </Box>

          <Box
            component="button"
            onClick={() => {
              window.open(`${process.env.REACT_APP_PICTORY_APP_URL}api-playground`, '_blank');
            }}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '16px',
              alignItems: 'center',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <Box
              sx={{
                width: '84px',
                height: '84px',
                backgroundColor: '#6F47CB',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'grey.0',
                transition: 'background-color 0.2s ease',
                '& svg': {
                  width: '60px',
                  height: '60px',
                },
                'button:hover &': {
                  backgroundColor: '#5939A2',
                },
              }}
            >
              <SearchThinSVGIcon />
            </Box>
            <Typography
              color="grey.9999"
              sx={{
                fontWeight: '400',
                maxWidth: '240px',
                textAlign: 'left',
                fontSize: '16px',
                'button:hover &': {
                  textDecoration: 'underline',
                },
              }}
            >
              Explore the API playground to test workflows
            </Typography>
          </Box>
        </Box>
      </motion.div>

      {showLoader && <LoadingPage setError={setError} />}
      {!showLoader && clientId && clientSecret && (
        <>
          {userApiKey && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              style={{
                width: '840px',
                margin: '0 auto',
                marginTop: '40px',
              }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #F0F7FF 0%, #E8F4FF 100%)',
                  border: '1px solid #B3D9FF',
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ fontSize: '20px', lineHeight: 1, marginTop: 0 }}>💡</Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'left', flex: 1 }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#0066CC', textAlign: 'left' }}>
                    API keys - Simpler and more secure
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#333', lineHeight: 1.5, textAlign: 'left' }}>
                    Use your API Key for authentication instead of Client ID and Secret. Simply include it in the{' '}
                    <strong style={{ color: '#0066CC' }}>Authorization</strong> header as a Bearer token for a
                    streamlined authentication experience.
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          )}

          {userApiKey && (
            <motion.div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                padding: '24px 0',
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                border: '3px solid rgb(241, 237, 250)',
                boxShadow: 'rgba(18, 18, 23, 0.04) 0px 2px 4px 0px, rgba(18, 18, 23, 0.04) 0px 5px 8px 0px',
                width: '840px',
                margin: '0 auto',
                marginTop: '24px',
                position: 'relative',
              }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '16px',
                  right: '24px',
                  background: 'linear-gradient(135deg, #5939A2 0%, #7C5CBF 100%)',
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '6px 12px',
                  borderRadius: '12px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <span>⭐</span> Recommended
              </Box>
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 700,
                  textAlign: 'left',
                  margin: 0,
                  borderBottom: '1px solid #E0E0E0',
                  padding: '0 24px',
                }}
                color="#5939a2"
              >
                API Keys
              </Typography>
              <Box sx={{ padding: '0 24px', gap: '24px', display: 'flex', flexDirection: 'column' }}>
                <CopyableField label="API Key" value={userApiKey} delay={0.5} />
              </Box>
            </motion.div>
          )}

          <motion.div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              padding: '24px 0',
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              border: '3px solid rgb(241, 237, 250)',
              boxShadow: 'rgba(18, 18, 23, 0.04) 0px 2px 4px 0px, rgba(18, 18, 23, 0.04) 0px 5px 8px 0px',
              width: '840px',
              margin: '0 auto',
              marginTop: '24px',
              position: 'relative',
            }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: userApiKey ? 0.5 : 0.3, duration: 0.6 }}
          >
            {userApiKey && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '16px',
                  right: '24px',
                  background: '#FFF4E5',
                  color: '#B54708',
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '4px 10px',
                  borderRadius: '12px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}
              >
                Legacy Support
              </Box>
            )}
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 700,
                textAlign: 'left',
                margin: 0,
                borderBottom: '1px solid #E0E0E0',
                padding: '0 24px',
              }}
              color="#5939a2"
            >
              API Credentials
            </Typography>
            <Box sx={{ padding: '0 24px', gap: '24px', display: 'flex', flexDirection: 'column' }}>
              <CopyableField label="Client ID" value={clientId} delay={0.5} />
              <CopyableField label="Client Secret" value={clientSecret} delay={0.7} />
            </Box>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 24px' }}>
              <Button
                variant="outlined"
                target="_blank"
                href={process.env.REACT_APP_PICTORY_APP_URL}
                style={{
                  backgroundColor: '#5939A2',
                  color: '#fff',
                  textTransform: 'none',
                  width: 'max-content',
                  borderRadius: '8px',
                  border: 'none',
                }}
                onMouseOver={e => (e.currentTarget.style.backgroundColor = '#6F47CB')}
                onMouseOut={e => (e.currentTarget.style.backgroundColor = '#5939A2')}
              >
                Login to your API
              </Button>
            </div>
          </motion.div>
        </>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          gap: '48px',
          alignItems: 'center',
          maxWidth: '840px',
          margin: '0 auto',
        }}
      >
        <Box sx={{ width: '300px' }}>
          <img
            src="https://pictory-static.pictorycontent.com/static/images/ApiSubscriptionsThumbSVGIcon.png"
            alt="API Subscriptions"
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: '400', textAlign: 'left' }} color="#2d2d2d">
            Review your quota and access your credentials directly{' '}
            <a
              href={process.env.REACT_APP_PICTORY_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#5939A2', textDecoration: 'none', fontWeight: '500' }}
            >
              from your Pictory account
            </a>
            . Once logged in, click on the top-right corner and select <strong>API subscription</strong> from the
            dropdown.
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: '400', textAlign: 'left' }} color="#2d2d2d">
            First time logging in? Check your email for instructions on for to set your Pictory password.
          </Typography>
        </Box>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        style={{ width: '100%' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '40px',
            maxWidth: '960px',
            margin: '0 auto',
            alignItems: 'center',
            padding: '72px 0 42px 0',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '40px', fontWeight: '600' }} color="#2d2d2d">
              Do more with Pictory integrations
            </Typography>
            <Typography sx={{ maxWidth: '730px', textAlign: 'center', fontSize: '18px' }} color="#2d2d2d">
              Unlock powerful video automation with our API and create AI-driven content faster than ever before by
              connecting to your preferred services.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '108px', alignItems: 'center' }}>
            {[
              {
                icon: <img src="/images/zapierLogo.svg" alt="Zapier" />,
                label: 'Explore our Zapier integration',
                url: INTEGRATION_URLS.ZAPIER,
              },
              {
                icon: <img src="/images/pictory.png" alt="Make" width={60} height={60} />,
                label: 'Explore our MCP server API',
                url: INTEGRATION_URLS.MCP_SERVER,
              },
              {
                icon: <img src="/images/makeLogo.svg" alt="Make" />,
                label: 'Explore our Make integration',
                url: INTEGRATION_URLS.MAKE,
              },
            ].map((integration, index) => (
              <Box
                key={index}
                onClick={() => window.open(integration.url, '_blank')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <Box
                  sx={{
                    minWidth: '84px',
                    minHeight: '84px',
                    backgroundColor: 'grey.0',
                    boxShadow: '0 4px 6px -1px rgba(18, 18, 23, 0.08), 0 2px 4px -1px rgba(18, 18, 23, 0.06)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {integration.icon}
                </Box>
                <Typography
                  color="grey.9999"
                  sx={{
                    fontWeight: '400',
                    textAlign: 'left',
                    fontSize: '18px',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {integration.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </motion.div>

      {/* <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        style={{
          textAlign: 'left',
          backgroundColor: '#f3f4f6', // gray-100
          padding: '1rem',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          color: '#374151', // gray-700
          lineHeight: '1.5',
        }}
      >
        <CredentialInstructions />
      </motion.div> */}
    </div>
  );
};

export default SuccessContent;
